import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCreateCampaign } from '../hooks/useARE'
import { useAICampaign } from '../hooks/useAICampaign'
import { ArrowLeft, Loader2, Zap, Sparkles, Check } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

function AIStar() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
      <path d="M8 0L9.8 6.2L16 8L9.8 9.8L8 16L6.2 9.8L0 8L6.2 6.2L8 0Z" fill="currentColor" />
    </svg>
  )
}

export function CampaignSetupPage() {
  const navigate = useNavigate()
  const create = useCreateCampaign()
  const ai = useAICampaign()
  const [applied, setApplied] = useState<string | null>(null)

  const [form, setForm] = useState({
    name: '',
    subject: '',
    email_body: '',
    promoter_threshold: 70,
    neutral_threshold: 40,
    trigger_events: ['deal_won'],
    delay_hours_min: 24,
    delay_hours_max: 72,
    send_hour_start: 9,
    send_hour_end: 11,
    gbp_review_url: '',
    max_sends_per_contact: 1,
    cooldown_days: 90,
  })

  const triggers = [
    { value: 'deal_won', label: 'Deal gagné' },
    { value: 'ticket_resolved', label: 'Ticket résolu' },
    { value: 'delivery_confirmed', label: 'Livraison confirmée' },
    { value: 'payment_received', label: 'Paiement reçu' },
  ]

  const toggleTrigger = (t: string) => {
    setForm(f => ({
      ...f,
      trigger_events: f.trigger_events.includes(t)
        ? f.trigger_events.filter(e => e !== t)
        : [...f.trigger_events, t],
    }))
  }

  const handleSubmit = async () => {
    if (!form.name) return
    try {
      await create.mutateAsync(form)
      navigate('/are')
    } catch (e) { console.error(e) }
  }

  const handleAIPropose = () => {
    ai.propose({
      business_name: 'Mon entreprise',
      business_type: 'entreprise locale',
      current_rating: 4.0,
      target_rating: 4.5,
      segment_description: form.trigger_events.join(', ') || 'clients récents',
    })
  }

  const handleApply = (field: string, value: string) => {
    if (field === 'subject_a' || field === 'subject_b') {
      setForm(f => ({ ...f, subject: value }))
    } else if (field === 'email_body') {
      setForm(f => ({ ...f, email_body: value }))
    }
    setApplied(field)
    setTimeout(() => setApplied(null), 2000)
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/are')}
          className="w-9 h-9 rounded-lg bg-paper-warm flex items-center justify-center hover:bg-ink-border/30 transition-colors">
          <ArrowLeft size={18} className="text-ink-muted" />
        </button>
        <div>
          <h1 className="font-serif text-2xl font-bold text-ink tracking-tight">Nouvelle campagne</h1>
          <p className="text-ink-muted text-sm">Configurez la collecte automatisée d'avis</p>
        </div>
      </div>

      <div className="bg-white rounded-card border border-ink-border divide-y divide-ink-border/30">
        {/* Name */}
        <div className="p-5">
          <label className="block text-sm font-semibold text-ink mb-2">Nom de la campagne</label>
          <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="ex : Collecte avis post-mission"
            className="w-full px-3 py-2.5 rounded-lg border border-ink-border text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
        </div>

        {/* Triggers */}
        <div className="p-5">
          <label className="block text-sm font-semibold text-ink mb-2">Événements déclencheurs</label>
          <div className="flex flex-wrap gap-2">
            {triggers.map(t => (
              <button key={t.value} onClick={() => toggleTrigger(t.value)}
                className={cn('px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors',
                  form.trigger_events.includes(t.value) ? 'bg-brick text-white border-brick' : 'bg-white text-ink-muted border-ink-border hover:border-gold')}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── AI Campaign Assistant ─────────────── */}
        <div className="p-5">
          <label className="block text-sm font-semibold text-ink mb-3">Email de la campagne</label>

          {/* AI Widget */}
          {!ai.proposal && !ai.isLoading ? (
            <button onClick={handleAIPropose} disabled={ai.isLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-ink-border rounded-card text-sm text-ink-muted hover:bg-paper transition-colors mb-4">
              <AIStar /> Générer avec l'IA
            </button>
          ) : ai.isLoading ? (
            <div className="animate-pulse bg-paper rounded-card h-32 w-full mb-4 flex items-center justify-center">
              <Loader2 size={20} className="animate-spin text-ink-faint" />
            </div>
          ) : ai.proposal ? (
            <div className="bg-paper border border-ink-border rounded-card p-4 mb-4 space-y-4 animate-fade-in">
              {/* Subjects A/B */}
              <div>
                <p className="text-[10px] text-ink-faint uppercase tracking-wider mb-2">Objets suggérés</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'A', value: ai.proposal.subject_a, field: 'subject_a' },
                    { label: 'B', value: ai.proposal.subject_b, field: 'subject_b' },
                  ].map((v) => (
                    <div key={v.label} className="bg-white rounded-lg border border-ink-border p-3">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="bg-onyx text-paper text-[9px] font-bold px-1.5 py-0.5 rounded">{v.label}</span>
                      </div>
                      <p className="text-sm text-ink mb-2">{v.value}</p>
                      <button onClick={() => handleApply(v.field, v.value)}
                        className="text-[11px] font-medium text-brick hover:text-brick-hover transition-colors flex items-center gap-1">
                        {applied === v.field ? <><Check size={12} className="text-emerald-500" /> Appliqué</> : 'Utiliser'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Email body */}
              <div>
                <p className="text-[10px] text-ink-faint uppercase tracking-wider mb-2">Corps de l'email</p>
                <div className="bg-white rounded-lg border border-ink-border p-3">
                  <p className="text-sm text-ink line-clamp-3">{ai.proposal.email_body}</p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-[10px] text-ink-faint italic">{'{{prenom}}'} sera remplacé automatiquement</p>
                    <button onClick={() => handleApply('email_body', ai.proposal!.email_body)}
                      className="text-[11px] font-medium text-brick hover:text-brick-hover transition-colors flex items-center gap-1">
                      {applied === 'email_body' ? <><Check size={12} className="text-emerald-500" /> Appliqué</> : "Appliquer dans l'éditeur"}
                    </button>
                  </div>
                </div>
              </div>

              {/* AI Advice */}
              <div className="bg-teal/5 border-l-2 border-teal rounded-r-lg p-3">
                <p className="text-[10px] text-teal/60 uppercase tracking-wider mb-1">Conseil IA</p>
                <p className="text-[11px] text-ink-muted leading-relaxed">{ai.proposal.send_timing}</p>
                <p className="text-[11px] text-ink-muted leading-relaxed mt-1">{ai.proposal.segment_advice}</p>
              </div>
            </div>
          ) : null}

          {ai.error && (
            <p className="text-xs text-brick mb-3">{ai.error}</p>
          )}

          {/* Subject field */}
          <div className="mb-3">
            <label className="block text-xs text-ink-muted mb-1">Objet de l'email</label>
            <input type="text" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
              placeholder="Objet de votre email..."
              className="w-full px-3 py-2.5 rounded-lg border border-ink-border text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
          </div>

          {/* Email body field */}
          <div>
            <label className="block text-xs text-ink-muted mb-1">Corps du message</label>
            <textarea value={form.email_body} onChange={e => setForm({ ...form, email_body: e.target.value })}
              rows={6} placeholder="Bonjour {{prenom}}, ..."
              className="w-full px-3 py-2.5 rounded-lg border border-ink-border text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold resize-none" />
          </div>
        </div>

        {/* Thresholds */}
        <div className="p-5 space-y-4">
          <label className="block text-sm font-semibold text-ink">Seuils de segmentation</label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-ink-muted mb-1">Seuil promoteur ({form.promoter_threshold})</label>
              <input type="range" min={30} max={100} step={5} value={form.promoter_threshold}
                onChange={e => setForm({ ...form, promoter_threshold: +e.target.value })}
                className="w-full accent-emerald-600" />
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Seuil neutre ({form.neutral_threshold})</label>
              <input type="range" min={10} max={70} step={5} value={form.neutral_threshold}
                onChange={e => setForm({ ...form, neutral_threshold: +e.target.value })}
                className="w-full accent-amber-500" />
            </div>
          </div>
        </div>

        {/* Timing */}
        <div className="p-5 space-y-4">
          <label className="block text-sm font-semibold text-ink">Timing d'envoi</label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-ink-muted mb-1">Délai min (heures)</label>
              <input type="number" value={form.delay_hours_min} min={1} max={168}
                onChange={e => setForm({ ...form, delay_hours_min: +e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-ink-border text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Délai max (heures)</label>
              <input type="number" value={form.delay_hours_max} min={1} max={168}
                onChange={e => setForm({ ...form, delay_hours_max: +e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-ink-border text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
            </div>
          </div>
        </div>

        {/* GBP Link */}
        <div className="p-5">
          <label className="block text-sm font-semibold text-ink mb-2">Lien avis Google</label>
          <input type="url" value={form.gbp_review_url} onChange={e => setForm({ ...form, gbp_review_url: e.target.value })}
            placeholder="https://g.page/votre-entreprise/review"
            className="w-full px-3 py-2.5 rounded-lg border border-ink-border text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
        </div>

        {/* Anti-spam */}
        <div className="p-5">
          <label className="block text-sm font-semibold text-ink mb-3">Protection anti-spam</label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-ink-muted mb-1">Max envois par contact</label>
              <select value={form.max_sends_per_contact} onChange={e => setForm({ ...form, max_sends_per_contact: +e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-ink-border text-sm focus:outline-none focus:ring-2 focus:ring-gold/50">
                <option value={1}>1 fois</option><option value={2}>2 fois</option><option value={3}>3 fois max</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Délai de repos (jours)</label>
              <input type="number" value={form.cooldown_days} min={7} max={365}
                onChange={e => setForm({ ...form, cooldown_days: +e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-ink-border text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-2">
        <button onClick={() => navigate('/are')}
          className="px-4 py-2.5 rounded-card text-sm font-medium text-ink-muted hover:bg-paper-warm transition-colors">Annuler</button>
        <button onClick={handleSubmit} disabled={create.isPending || !form.name}
          className="flex items-center gap-2 rounded-card bg-brick px-6 py-2.5 text-sm font-semibold text-white hover:bg-brick-hover transition-colors disabled:opacity-50">
          {create.isPending ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
          Créer la campagne
        </button>
      </div>
    </div>
  )
}
